import { phpJsonNumberString } from './php-json-number-string';
import { phpJsonInteger } from './php-integer-arithmetic';

/** PHP !empty for JSON decoded into associative arrays, including the special string '0'. */
export function phpJsonTruthy(value: unknown): boolean {
    return !(value === null || value === undefined || value === false || value === '' || value === '0' || value === 0
        || (typeof value === 'object' && Object.keys(value).length === 0));
}

/** PHP float coercion for JSON values retained in lean compile rows. */
export function phpJsonNumber(value: unknown): number {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'boolean') return value ? 1 : 0;
    if (typeof value === 'object') return Object.keys(value).length > 0 ? 1 : 0;
    const numeric = typeof value === 'number' ? value
        : Number(String(value).match(/^[\t\n\r\v\f ]*[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?/i)?.[0] ?? 0);
    return Number.isFinite(numeric) ? numeric : 0;
}

/** PHP integer cast after JSON decoding: strings saturate, out-of-range floats wrap at 64 bits. */
export function phpJsonInt(value: unknown): number {
    return Number(phpJsonInteger(value));
}

/** String conversion for JSON values decoded as associative arrays by PHP. */
export function phpJsonString(value: unknown): string {
    if (value === null || value === undefined || value === false) return '';
    if (value === true) return '1';
    if (typeof value === 'object') return 'Array';
    if (typeof value === 'number') return phpJsonNumberString(value);
    return String(value);
}

/** PHP's string spaceship comparison: numeric strings compare numerically; other
 * valid Unicode strings compare in UTF-8 byte order (equivalent to code-point order).
 */
export function comparePhpStrings(left: string, right: string): number {
    const numericPattern = /^[\t\n\r\v\f ]*[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?[\t\n\r\v\f ]*$/i;
    if (numericPattern.test(left) && numericPattern.test(right)) {
        // PHP keeps integer strings exact through its signed 64-bit integer range.
        const integerPattern = /^[\t\n\r\v\f ]*[+-]?\d+[\t\n\r\v\f ]*$/;
        if (integerPattern.test(left) && integerPattern.test(right)) {
            const a = BigInt(left.trim()), b = BigInt(right.trim());
            const minimum = -9223372036854775808n, maximum = 9223372036854775807n;
            if (a >= minimum && a <= maximum && b >= minimum && b <= maximum) {
                return a < b ? -1 : a > b ? 1 : 0;
            }
        }
        const a = Number(left), b = Number(right);
        if (Number.isFinite(a) && Number.isFinite(b)) return a < b ? -1 : a > b ? 1 : 0;
    }
    const a = Array.from(left), b = Array.from(right);
    for (let index = 0; index < Math.min(a.length, b.length); index++) {
        const difference = a[index]!.codePointAt(0)! - b[index]!.codePointAt(0)!;
        if (difference !== 0) return difference;
    }
    return a.length - b.length;
}
